// commitEdit — round-trip an `SR-Edit` `StructuralEdit` through Monaco.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Commit Pipeline.
//
// Pipeline:
//   1. `resolveEdit` (SR-Edit `applyStructuralEdit` + `emitSource`)
//   2. coalesce coincident edits + defense-in-depth non-overlap check
//   3. `applyPlannedEditsWithFeedback`: convert byte edits to Monaco
//      `IIdentifiedSingleEditOperation[]` and call
//      `editor.executeEdits('vqe-edit', ...)` so the undo stop carries the
//      `'vqe-edit'` source label, falling back to `model.pushEditOperations`
//      when no editor reference is supplied (used in tests), then
//      `service.onTextChange` so the next edit sees fresh structure.
//
// Every edit applies directly — there is no preview-before-commit step. Undo
// is the safety net: Monaco's own undo stack (the `'vqe-edit'` undo stop
// above), in the Visual view and the compact popover alike.
//
// Outcome contract (P12 §4.2): `commitEdit` NEVER throws. The WHOLE pipeline
// (resolve + coalesce + non-overlap assert + apply/emit tail) runs inside one
// feedback path — every failure records a `commitFeedback` failure, fires
// `feature:vqe_commit_failed { op }`, and RESOLVES `{ status: 'failed' }`. This
// is the single outcome chokepoint: the ~25 fire-and-forget `void edit(…)` call
// sites can no longer leak an unhandled rejection into the crash dialog.
//
// `commitEdit` is the ONLY module that mutates a Monaco model. Edit-action
// modules (addColumn / addJoin / etc., shipped in later slices) build
// `StructuralEdit` values and delegate. (Codebase Pattern in PROGRESS.md.)

import type {
  PlannedByteEdit,
  StructuralEdit,
} from '../../../sql-resolver/types';
import { useVqeStore } from '../store/vqeStore';
import { vqeCopy } from '../ux/vqeCopy';
import { resolveEdit, type ResolveEditCtx } from './resolveEdit';
import { trackVqeEvent } from '../ux/trackVqeEvent';

export const VQE_UNDO_SOURCE = 'vqe-edit';

export type MonacoSingleEditOperation = {
  readonly range: {
    readonly startLineNumber: number;
    readonly startColumn: number;
    readonly endLineNumber: number;
    readonly endColumn: number;
  };
  readonly text: string;
};

/** Minimal Monaco `ITextModel` surface commitEdit needs. */
export type MonacoTextModelLike = {
  readonly getPositionAt: (offset: number) => {
    readonly lineNumber: number;
    readonly column: number;
  };
  readonly pushEditOperations: (
    beforeCursorState: ReadonlyArray<unknown> | null,
    edits: ReadonlyArray<MonacoSingleEditOperation>,
    cursorStateComputer: () => ReadonlyArray<unknown> | null,
  ) => unknown;
};

/** Minimal Monaco `IStandaloneCodeEditor` surface commitEdit needs. */
export type MonacoEditorLike = {
  readonly executeEdits: (
    source: string,
    edits: ReadonlyArray<MonacoSingleEditOperation>,
  ) => boolean;
};

export type CommitEditCtx = ResolveEditCtx & {
  readonly model: MonacoTextModelLike;
  readonly editor?: MonacoEditorLike;
  /**
   * Authoritative CURRENT buffer text (Monaco editor value / workspace-store
   * tab query) — the text the user actually sees, which can run AHEAD of the
   * resolver's parsed source while the async re-parse catches up (P12 §e
   * stale-buffer race). When supplied, `commitEdit`:
   *   - FLUSH: re-syncs the resolver to it (`service.onTextChange`) BEFORE
   *     resolving, so the planned edits are computed against what the user sees;
   *   - GUARD: immediately before splicing, re-reads it and refuses to splice
   *     (failed outcome, never a crash, never a double-apply) if it moved again
   *     since the resolve — see `applyPlannedEditsWithFeedback`.
   * When absent, behavior is byte-identical to the pre-Task-4 baseline.
   */
  readonly getLiveSource?: () => string;
  /**
   * Suppress the success `vqe_edit_commit` event for THIS commit. Set by
   * composed multi-commit gestures (modifyOrderBy's add+remove chain) so one
   * user gesture counts once — the chain fires its own gesture-level event
   * via `trackEditCommit` after the last commit applies (2026-07-25 volume
   * audit: the chain double-counted every direction toggle). Failure
   * analytics (`vqe_commit_failed`) are NEVER suppressed — each failed chain
   * step stays diagnostic.
   */
  readonly suppressCommitAnalytics?: boolean;
};

/**
 * Non-throwing outcome of a commit attempt (P12 §4.2):
 *  - `applied` — the edit reached the Monaco buffer (or was a clean no-op),
 *  - `failed`  — a phase threw; feedback + failure analytics were already
 *                recorded and `error` carries the original cause.
 */
export type CommitOutcome =
  | { readonly status: 'applied' }
  | { readonly status: 'failed'; readonly error: unknown };

/** Coalesced + validated planned edits plus the sources the apply tail needs. */
type PreparedEdit = {
  readonly plannedEdits: ReadonlyArray<PlannedByteEdit>;
  readonly emittedSource: string;
  readonly originalSource: string;
};

export async function commitEdit(
  edit: StructuralEdit,
  ctx: CommitEditCtx,
): Promise<CommitOutcome> {
  let prepared: PreparedEdit;
  // The resolve→coalesce→assert phase is as fault-prone as the apply tail
  // (missing document, DDL statement, an invariant-violating overlap), so it
  // runs inside the same feedback path — a throw here becomes a `failed`
  // outcome, never an unhandled rejection.
  try {
    // Task 4 FLUSH — if the authoritative live buffer has moved ahead of the
    // resolver's parsed source (the user typed in Monaco / the workspace store
    // updated before the async re-parse landed), re-sync the resolver to it
    // BEFORE resolving so the planned edits are computed against what the user
    // actually sees. A throw here is caught below and recorded like any other
    // pipeline failure. Skipped when the document isn't open yet — `resolveEdit`
    // then produces the canonical "no document" failure instead.
    if (ctx.getLiveSource) {
      const live = ctx.getLiveSource();
      const parsedSource =
        ctx.service.getParsedBuffer(ctx.documentId)?.parsedDocument.source;
      if (parsedSource !== undefined && parsedSource !== live) {
        await ctx.service.onTextChange(ctx.documentId, live);
      }
    }

    const resolved = resolveEdit(edit, ctx);
    const { modified, emittedSource, originalSource } = resolved;

    // A zero-planned-edits resolve is a clean no-op: nothing to apply, nothing
    // to count, and no feedback. Behavior-preserving — report `applied`.
    if (modified.plannedEdits.length === 0) return { status: 'applied' };

    // Coalesce a delete + zero-width insert at the same start offset into a
    // single replacement before sending edits to Monaco. SR-Edit's
    // modify-join-on planner emits this pair (delete the existing predicate
    // range, then insert the new text at the same offset); semantically it is
    // one replacement. Coalescing also keeps `assertNonOverlappingByteEdits`
    // honest — the pair counts as a true overlap in the naive byte-range sense.
    const plannedEdits = coalesceCoincidentEdits(modified.plannedEdits);

    // Defense in depth — SR-Edit's `emitSource` already validates non-overlap
    // before splicing, but we re-check here so an invariant-violating edit
    // can't reach the Monaco buffer even if SR-Edit ever regresses.
    assertNonOverlappingByteEdits(plannedEdits);

    prepared = { plannedEdits, emittedSource, originalSource };
  } catch (error) {
    return recordFailureOutcome(edit, error);
  }

  return applyPlannedEditsWithFeedback(
    edit,
    prepared.plannedEdits,
    prepared.emittedSource,
    prepared.originalSource,
    ctx,
  );
}

/**
 * Apply tail — applies the already-coalesced planned edits to Monaco,
 * re-syncs the resolver, records commit feedback (success + failure), and fires
 * `vqe_edit_commit` / `vqe_commit_failed`. Non-throwing: returns a
 * `CommitOutcome`.
 *
 * `originalSource` is the buffer text the planned edits were resolved against.
 * When `ctx.getLiveSource` is supplied, the Task-4 GUARD compares it to the
 * authoritative live buffer immediately before splicing: if the live buffer
 * moved since the resolve, the offset-based `plannedEdits` no longer line up, so
 * re-applying them would corrupt (or, on a post-partial-failure retry,
 * double-apply) the SQL. In that case we record a specific failure and splice
 * NOTHING.
 */
export async function applyPlannedEditsWithFeedback(
  edit: StructuralEdit,
  plannedEdits: ReadonlyArray<PlannedByteEdit>,
  emittedSource: string,
  originalSource: string,
  ctx: CommitEditCtx,
): Promise<CommitOutcome> {
  if (ctx.getLiveSource && ctx.getLiveSource() !== originalSource) {
    // The live buffer moved between resolve and apply — never splice against a
    // stale plan. Failed outcome carrying the specific stale-buffer copy; the
    // model is left untouched.
    return recordFailureOutcome(edit, new Error(vqeCopy.toast.staleBuffer));
  }
  try {
    applyResolvedEditToMonaco(plannedEdits, ctx);
    await ctx.service.onTextChange(ctx.documentId, emittedSource);
  } catch (error) {
    return recordFailureOutcome(edit, error);
  }
  useVqeStore.getState().recordCommitFeedback({ kind: 'success', at: Date.now(), documentId: ctx.documentId });
  // Analytics — the edit truly reached the buffer; this tail is the single
  // chokepoint. `edit.op` is the low-cardinality `StructuralEdit` discriminant
  // — no PII.
  if (!ctx.suppressCommitAnalytics) trackEditCommit(edit.op);
  return { status: 'applied' };
}

/**
 * Record a commit failure the same way regardless of which phase threw:
 * feedback toast payload + failure analytics + a `failed` outcome carrying the
 * original error. Never re-throws (that is the whole point of P12 §4.2).
 */
function recordFailureOutcome(
  edit: StructuralEdit,
  error: unknown,
): CommitOutcome {
  useVqeStore.getState().recordCommitFeedback({
    kind: 'failure',
    at: Date.now(),
    message: error instanceof Error ? error.message : String(error),
  });
  trackVqeEvent('feature:vqe_commit_failed', { op: edit.op });
  return { status: 'failed', error };
}

/** Commit analytics. Exported for composed gestures (modifyOrderBy) that
 *  suppress their per-step events and fire one gesture-level op instead — the
 *  extra `'modify-order-by'` value keeps the `op` prop low-cardinality. */
export function trackEditCommit(op: StructuralEdit['op'] | 'modify-order-by'): void {
  trackVqeEvent('feature:vqe_edit_commit', { op });
}

function coalesceCoincidentEdits(
  edits: ReadonlyArray<PlannedByteEdit>,
): ReadonlyArray<PlannedByteEdit> {
  if (edits.length < 2) return edits;
  const sorted = [...edits].sort((a, b) => {
    if (a.range.startOffset !== b.range.startOffset) {
      return a.range.startOffset - b.range.startOffset;
    }
    // Wider range first, so a delete sorts before a zero-width insert at
    // the same start offset (lets the merged result use the delete's range).
    return b.range.endOffset - a.range.endOffset;
  });
  const out: PlannedByteEdit[] = [];
  for (const edit of sorted) {
    const prev = out[out.length - 1];
    if (
      prev &&
      prev.range.startOffset === edit.range.startOffset &&
      edit.range.startOffset === edit.range.endOffset
    ) {
      out[out.length - 1] = {
        range: prev.range,
        replacement: prev.replacement + edit.replacement,
        rationale: `${prev.rationale}+${edit.rationale}`,
      };
      continue;
    }
    out.push(edit);
  }
  return out;
}

function applyResolvedEditToMonaco(
  plannedEdits: ReadonlyArray<PlannedByteEdit>,
  ctx: CommitEditCtx,
): void {
  const monacoEdits = plannedEdits.map((edit) =>
    byteEditToMonacoEdit(edit, ctx.model),
  );
  if (ctx.editor) {
    ctx.editor.executeEdits(VQE_UNDO_SOURCE, monacoEdits);
    return;
  }
  ctx.model.pushEditOperations(null, monacoEdits, () => null);
}

/** Buffer offsets → the Monaco line/column range covering them. */
export function rangeFromOffsets(model: MonacoTextModelLike, startOffset: number, endOffset: number) {
  const start = model.getPositionAt(startOffset);
  const end = model.getPositionAt(endOffset);
  return {
    startLineNumber: start.lineNumber,
    startColumn: start.column,
    endLineNumber: end.lineNumber,
    endColumn: end.column,
  };
}

function byteEditToMonacoEdit(
  edit: PlannedByteEdit,
  model: MonacoTextModelLike,
): MonacoSingleEditOperation {
  return {
    range: rangeFromOffsets(model, edit.range.startOffset, edit.range.endOffset),
    text: edit.replacement,
  };
}

function assertNonOverlappingByteEdits(
  edits: ReadonlyArray<PlannedByteEdit>,
): void {
  const sorted = [...edits].sort(
    (a, b) => a.range.startOffset - b.range.startOffset,
  );
  for (let i = 0; i + 1 < sorted.length; i++) {
    const cur = sorted[i]!;
    const nxt = sorted[i + 1]!;
    if (nxt.range.startOffset < cur.range.endOffset) {
      throw new Error(
        `commitEdit: overlapping plannedByteEdits — [${cur.range.startOffset}, ${cur.range.endOffset}) and [${nxt.range.startOffset}, ${nxt.range.endOffset})`,
      );
    }
  }
}
